import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type {
	OnKeyDownHandler,
	OnKeyDownHandlerSetter,
	SlashCommandDefinition,
	SlashCommandProps,
	SlashCommandSuggestionImpl,
} from "./types"
import type { SuggestionProps } from "@tiptap/suggestion"
import { suggestionPopupTarget } from "../suggestion-popup-target"
import {
	bulletedListCommand,
	checklistCommand,
	codeBlockCommand,
	codeGroupCommand,
	dangerCalloutCommand,
	detailsListCommand,
	dividerCommand,
	embedCommand,
	heading1Command,
	heading2Command,
	heading3Command,
	imageGalleryCommand,
	loomVideoCommand,
	mediaCommand,
	numberedListCommand,
	quoteCommand,
	tableAddColumnCommand,
	tableAddRowCommand,
	tableCommand,
	tableRemoveColumnCommand,
	tableRemoveRowCommand,
	tabsCommand,
	cardCommand,
	cardGroupCommand,
	warningCalloutCommand,
	youtubeVideoCommand,
	noteCalloutCommand,
	infoCalloutCommand,
	tipCalloutCommand,
	checkCalloutCommand,
	badgeCommand,
	tooltipCommand,
	iconCommand,
	paramFieldCommand,
	responseFieldCommand,
	expandableCommand,
	updateCommand,
	tilesCommand,
	treeCommand,
	frameCommand,
	mermaidCommand,
	promptCommand,
	colorSwatchesCommand,
	colorTableCommand,
} from "./commands"
import SlashCommandsList from "./slash-commands-list"

// Grouped and ordered like Linear's menu, short enough to show without scrolling.
export const slashCommandItems: SlashCommandDefinition[] = [
	// Headings
	heading1Command,
	heading2Command,
	heading3Command,
	// Lists
	bulletedListCommand,
	numberedListCommand,
	checklistCommand,
	// Media
	mediaCommand,
	imageGalleryCommand,
	youtubeVideoCommand,
	loomVideoCommand,
	embedCommand,
	// Blocks
	codeBlockCommand,
	codeGroupCommand,
	tabsCommand,
	cardCommand,
	cardGroupCommand,
	tilesCommand,
	tableCommand,
	detailsListCommand,
	expandableCommand,
	quoteCommand,
	noteCalloutCommand,
	infoCalloutCommand,
	tipCalloutCommand,
	checkCalloutCommand,
	warningCalloutCommand,
	dangerCalloutCommand,
	frameCommand,
	updateCommand,
	treeCommand,
	mermaidCommand,
	promptCommand,
	colorSwatchesCommand,
	colorTableCommand,
	badgeCommand,
	tooltipCommand,
	iconCommand,
	// API docs
	paramFieldCommand,
	responseFieldCommand,
	dividerCommand,
	// Table, only inside a table
	tableAddRowCommand,
	tableAddColumnCommand,
	tableRemoveRowCommand,
	tableRemoveColumnCommand,
]

// Table edits only make sense with the cursor in a table, so they stay out of the menu elsewhere.
export const filterSlashCommands = (
	items: SlashCommandDefinition[],
	query: string,
	inTable: boolean,
) => {
	const lowerQuery = query.toLocaleLowerCase()
	return items.filter((item) => {
		if (item.group === "table" && !inTable) return false
		return (
			item.title.toLocaleLowerCase().includes(lowerQuery) ||
			item.aliases?.some((alias) =>
				alias.toLocaleLowerCase().includes(lowerQuery),
			)
		)
	})
}

export const suggestion: SlashCommandSuggestionImpl = {
	items: ({ query, editor }) =>
		filterSlashCommands(slashCommandItems, query, editor.isActive("table")),

	render: () => {
		let component: ReactRenderer<
			unknown,
			SuggestionProps<SlashCommandDefinition, SlashCommandProps>
		> | null = null
		let popup: Instance<Props>[] | null = null
		let onKeyDownHandler: OnKeyDownHandler = () => true
		const setOnKeyDownHandler: OnKeyDownHandlerSetter = (handler) => {
			onKeyDownHandler = handler
		}

		return {
			onStart: (props) => {
				component = new ReactRenderer(SlashCommandsList, {
					props: {
						...props,
						setOnKeyDownHandler,
					},
					editor: props.editor,
				})

				if (!props.clientRect) {
					return
				}

				const target = suggestionPopupTarget(props.editor)

				// @ts-expect-error tippy types are wrong
				popup = tippy("body", {
					getReferenceClientRect: props.clientRect,
					appendTo: () => target,
					content: component.element,
					showOnCreate: true,
					interactive: true,
					trigger: "manual",
					placement: "top-start",
					popperOptions: {
						strategy: "fixed",
						modifiers: [
							{
								name: "flip",
								options: { fallbackPlacements: ["bottom-start", "top-start"] },
							},
							{
								name: "preventOverflow",
								options: {
									padding: 8,
									altAxis: true,
									boundary:
										target === document.body ? "clippingParents" : target,
								},
							},
						],
					},
				})

				const resizeObserver = new ResizeObserver(() => {
					if (!popup?.[0]) return

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					popup[0].popperInstance?.update()
				})

				resizeObserver.observe(component.element)

				const containers = document.querySelectorAll(".scroll-container")
				containers.forEach((el) => {
					;(el as HTMLElement).style.overflow = "hidden"
				})
			},

			onUpdate(props) {
				if (!component) return
				component.updateProps(props)

				const { clientRect } = props
				if (!clientRect) {
					return
				}

				popup?.[0]?.setProps({
					getReferenceClientRect: () => {
						const c = clientRect()
						if (!c) {
							throw new Error("clientRect is null")
						}
						return c
					},
				})
			},

			onKeyDown(props) {
				if (props.event.key === "Escape") {
					popup?.[0]?.hide()
					return true
				}

				return onKeyDownHandler(props)
			},

			onExit() {
				popup?.[0]?.destroy()
				component?.destroy()
				document.body.classList.remove("slash-commands-open")
				document.documentElement.classList.remove("slash-commands-open")

				const containers = document.querySelectorAll(".scroll-container")
				containers.forEach((el) => {
					;(el as HTMLElement).style.overflow = ""
				})
			},
		}
	},
}
